import React from 'react';
import { Search, X, Store as StoreIcon, ArrowUpDown, Bot, Clock, UserCheck, CheckCircle2, MessageSquare } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useGetApiWorkspacesWorkspaceIdStores } from '@/api/generated/stores/stores';
import { useConversationStore, type ConversationStatusFilter, type ConversationSortOrder } from '@/stores/useConversationStore';
import type { StoreItem } from '@/types';
import { cn, extractStoresArray } from '@/lib/utils';
import { useUnreadSummary } from '@/hooks/useUnreadSummary';
import { Badge } from '@/components/ui/badge';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';

const STATUS_PILLS: { id: ConversationStatusFilter; label: string; icon: React.FC<{ className?: string }> }[] = [
  { id: 'ALL', label: 'Todas', icon: MessageSquare },
  { id: 'UNREAD', label: 'Não Lidas', icon: MessageSquare },
  { id: 'WAITING', label: 'Aguardando', icon: Clock },
  { id: 'AI_HANDLING', label: 'Atendimento IA', icon: Bot },
  { id: 'HUMAN_ASSIGNED', label: 'Atendimento Humano', icon: UserCheck },
  { id: 'CLOSED', label: 'Fechadas', icon: CheckCircle2 },
];

export const ConversationFilters: React.FC<{ className?: string }> = ({ className }) => {
  const { activeWorkspaceId } = useWorkspaceStore();
  const {
    selectedStoreId,
    setSelectedStoreId,
    statusFilter,
    setStatusFilter,
    searchKeyword,
    setSearchKeyword,
    sortOrder,
    setSortOrder,
    customerFilterId,
    customerFilterName,
    setCustomerFilter,
    resetFilters,
  } = useConversationStore();

  const { data: storesData, isLoading: isLoadingStores } = useGetApiWorkspacesWorkspaceIdStores<StoreItem[]>(
    activeWorkspaceId!,
    { query: { enabled: !!activeWorkspaceId } }
  );
  const { storeCounts } = useUnreadSummary();
  const stores = extractStoresArray(storesData) as StoreItem[];

  const hasActiveFilters =
    Boolean(selectedStoreId) ||
    statusFilter !== 'ALL' ||
    Boolean(searchKeyword.trim()) ||
    Boolean(customerFilterId) ||
    sortOrder !== 'NEWEST_ACTIVITY';

  return (
    <div className={cn('p-3 border-b border-border/80 bg-card space-y-3', className)}>
      {/* Top Controls: Store Selector & Sort Dropdown */}
      <div className="flex items-center gap-2">
        {/* Store Selector */}
        <div className="flex-1 min-w-0">
          <Select
            value={selectedStoreId || 'ALL'}
            onValueChange={(val) => setSelectedStoreId(val === 'ALL' ? null : val)}
            disabled={isLoadingStores}
          >
            <SelectTrigger className="h-8 text-xs font-medium w-full bg-background">
              <div className="flex items-center gap-1.5 truncate">
                <StoreIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <SelectValue placeholder="Todas as Lojas" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL" className="text-xs">
                Todas as Lojas
              </SelectItem>
              {stores.map((store) => {
                const displayName = store.name || store.storeName || 'Loja sem Nome';
                const displayProvider = store.provider || store.platform;
                return (
                  <SelectItem key={store.id} value={store.id} className="text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{displayName}</span>
                      {storeCounts[store.id] > 0 && <Badge variant="default" className="text-[10px] px-1 py-0 h-4 ml-2">{storeCounts[store.id]}</Badge>}
                      {displayProvider && (
                        <span className="text-[10px] text-muted-foreground uppercase">
                          ({displayProvider})
                        </span>
                      )}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        {/* Sort Order Selector */}
        <div className="w-36 shrink-0">
          <Select
            value={sortOrder}
            onValueChange={(val) => setSortOrder(val as ConversationSortOrder)}
          >
            <SelectTrigger className="h-8 text-xs font-medium bg-background">
              <div className="flex items-center gap-1.5 truncate">
                <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="NEWEST_ACTIVITY" className="text-xs">
                Atividade Mais Recente
              </SelectItem>
              <SelectItem value="OLDEST_WAITING" className="text-xs">
                Mais Tempo Aguardando (SLA)
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Search Input Bar */}
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        <Input
          type="text"
          placeholder="Buscar cliente, telefone ou tópico..."
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          className="pl-8 pr-8 h-8 text-xs bg-background"
        />
        {searchKeyword && (
          <button
            type="button"
            onClick={() => setSearchKeyword('')}
            className="absolute right-2 top-2 h-4 w-4 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground"
            aria-label="Limpar busca"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Active Customer Filter Pill */}
      {customerFilterId && (
        <div className="flex items-center gap-2 pb-1">
          <Badge variant="secondary" className="px-2 py-1 flex items-center gap-1.5 text-xs font-medium border-primary/20 bg-primary/10 text-primary">
            <UserCheck className="h-3.5 w-3.5" />
            <span>Cliente: {customerFilterName || customerFilterId}</span>
            <button
              onClick={() => setCustomerFilter(null, null)}
              className="ml-1 rounded-full p-0.5 hover:bg-primary/20 transition-colors"
              aria-label="Limpar filtro de cliente"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        </div>
      )}

      {/* Status Filter Pills */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none no-scrollbar">
        {STATUS_PILLS.map((pill) => {
          const Icon = pill.icon;
          const isActive = statusFilter === pill.id;
          return (
            <button
              key={pill.id}
              type="button"
              onClick={() => setStatusFilter(pill.id)}
              className={cn(
                'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors border',
                isActive
                  ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                  : 'bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground border-transparent'
              )}
            >
              <Icon className="h-3 w-3" />
              <span>{pill.label}</span>
            </button>
          );
        })}
      </div>

      {/* Reset Filter Button if active */}
      {hasActiveFilters && (
        <div className="flex items-center justify-between text-[11px] text-muted-foreground pt-0.5">
          <span>Filtros ativos aplicados</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={resetFilters}
            className="h-5 px-1.5 text-[11px] text-primary hover:text-primary/80"
          >
            Limpar Filtros
          </Button>
        </div>
      )}
    </div>
  );
};

export default ConversationFilters;

